import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import {
  HpiDataTable,
  HpiDateFilterBar,
  HpiFilterSelect,
  HpiPagination,
  HpiStatsBar,
} from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface ChangeEntry {
  id: number
  change_type: string | null
  severity: "critical" | "important" | null
  customer_name: string | null
  customer_id: number | null
  contact_name: string | null
  customer_user_id: number | null
  changed_by: string | null
  changed_at: string | null
  old_email: string | null
  new_email: string | null
}

interface Summary {
  total_entries: number
}

interface Props {
  changes: ChangeEntry[]
  start_date: string
  end_date: string
  email: string
  status: string
  summary: Summary
}

/* ── Helpers ────────────────────────────────────────────── */

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return ""
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "numeric",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
}

/* ── Severity badge styles ────────────────────────────── */

function severityClasses(severity: string | null): string {
  switch (severity) {
    case "critical":
      return "bg-red-700 text-yellow-300 font-bold px-2 py-0.5 rounded"
    case "important":
      return "text-red-700 font-bold"
    default:
      return ""
  }
}

/* ── Columns ───────────────────────────────────────────── */

const PER_PAGE = 15

const STATUS_OPTIONS = [
  { value: "Pending", label: "Pending" },
  { value: "Completed", label: "Completed" },
]

/* ── Main Component ────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Reports", href: "/reports/email_changes" },
  { title: "Email Changes", href: "/reports/email_changes" },
]

export default function EmailChangesReport({
  changes,
  start_date,
  end_date,
  email,
  status,
  summary,
}: Props) {
  const [startDate, setStartDate] = useState(start_date)
  const [endDate, setEndDate] = useState(end_date)
  const [emailFilter, setEmailFilter] = useState(email)
  const [selectedStatus, setSelectedStatus] = useState(status)
  const [page, setPage] = useState(1)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isPending = status === "Pending"
  const totalPages = Math.ceil(changes.length / PER_PAGE)
  const paginated = changes.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  const allPageSelected =
    paginated.length > 0 && paginated.every((r) => selectedIds.has(r.id))

  const toggleOne = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        for (const r of paginated) next.delete(r.id)
        return next
      })
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        for (const r of paginated) next.add(r.id)
        return next
      })
    }
  }

  const handleMarkDone = () => {
    if (selectedIds.size === 0) return
    setIsSubmitting(true)
    router.post(
      "/reports/email_changes/bulk_complete",
      { ids: Array.from(selectedIds) },
      {
        onFinish: () => {
          setIsSubmitting(false)
          setSelectedIds(new Set())
        },
      },
    )
  }

  const handleUpdate = () => {
    setPage(1)
    setSelectedIds(new Set())
    router.get(
      "/reports/email_changes",
      {
        start_date: startDate,
        end_date: endDate,
        email: emailFilter,
        status: selectedStatus,
      },
      { preserveState: true },
    )
  }

  const columns: HpiColumn<ChangeEntry>[] = [
    ...(isPending
      ? [
          {
            header: "",
            width: "w-[40px]",
            render: (row: ChangeEntry) => (
              <input
                type="checkbox"
                checked={selectedIds.has(row.id)}
                onChange={() => toggleOne(row.id)}
                className="h-4 w-4 rounded border-gray-300"
              />
            ),
          } satisfies HpiColumn<ChangeEntry>,
        ]
      : []),
    {
      header: "Message",
      width: "max-w-[180px]",
      render: (row) => (
        <div className="text-sm">
          <span className={severityClasses(row.severity)}>
            {row.change_type ?? "-"}
          </span>
        </div>
      ),
    },
    {
      header: "Contact",
      render: (row) => (
        <div className="text-sm">
          <div>
            {row.contact_name && <span>{row.contact_name} at </span>}
            {row.customer_name}
          </div>
          <div className="text-muted-foreground text-xs">
            {row.changed_by} at {formatDateTime(row.changed_at)}
          </div>
        </div>
      ),
    },
    {
      header: "Old Email",
      width: "max-w-[220px]",
      render: (row) => <div className="text-sm">{row.old_email ?? "-"}</div>,
    },
    {
      header: "New Email",
      width: "max-w-[220px]",
      render: (row) => <div className="text-sm">{row.new_email ?? "-"}</div>,
    },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Email Change Log" />

      <div className="p-6">
        {/* Filter Bar */}
        <HpiDateFilterBar
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          onUpdate={handleUpdate}
          className="mb-6"
        >
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Email</label>
            <input
              type="text"
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              placeholder="Search email..."
              className="border-input bg-background h-9 rounded-md border px-3 text-sm"
            />
          </div>
          <HpiFilterSelect
            label="Status"
            value={selectedStatus}
            onChange={setSelectedStatus}
            options={STATUS_OPTIONS}
          />
        </HpiDateFilterBar>

        {/* Select All + Mark Done bar (only when Pending) */}
        {isPending && changes.length > 0 && (
          <div className="mb-2 flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={allPageSelected}
                onChange={toggleAll}
                className="h-4 w-4 rounded border-gray-300"
              />
              Select all on page
            </label>
            {selectedIds.size > 0 && (
              <button
                onClick={handleMarkDone}
                disabled={isSubmitting}
                className="bg-hpi-dark-blue hover:bg-hpi-dark-blue/90 rounded px-4 py-1.5 text-sm font-medium text-white disabled:opacity-50"
              >
                {isSubmitting ? "Marking…" : `Mark Done (${selectedIds.size})`}
              </button>
            )}
          </div>
        )}

        {/* Data Table */}
        {changes.length >= 500 && (
          <p className="mb-2 text-sm font-semibold text-red-600">
            First 500 shown, filter to refine search
          </p>
        )}
        <HpiDataTable<ChangeEntry>
          title="Email Changes"
          columns={columns}
          data={paginated}
          keyFn={(row) => row.id}
          pagination={
            changes.length > PER_PAGE ? (
              <HpiPagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={changes.length}
                perPage={PER_PAGE}
                onPageChange={setPage}
              />
            ) : undefined
          }
        />

        {/* Summary Stats */}
        <HpiStatsBar
          className="mt-6"
          stats={[
            {
              label: "Total",
              subLabel: "Entries",
              value: summary.total_entries,
            },
          ]}
        />
      </div>
    </AppLayout>
  )
}
